import React, { useCallback, useEffect, useState } from 'react';
import { PackageIcon, SearchIcon, PlusIcon, AlertTriangleIcon, BarChart3Icon, EditIcon, TrashIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { supabaseAdmin } from '../lib/supabase';
import { Product } from '../types';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { useAuth } from '../context/AuthContext';
// SKU auto-increment prefix
const SKU_PREFIX = 'PRD';
type FormData = {
  nombre: string;
  sku: string;
  stock: string;
  precio: string;
  subTotal: string;
  itbis: string;
  proveedor: string;
  fechaVencimiento: string;
};
const emptyForm: FormData = {
  nombre: '',
  sku: '',
  stock: '',
  precio: '',
  subTotal: '',
  itbis: '',
  proveedor: '',
  fechaVencimiento: ''
};
export function InventarioPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const canEdit = user?.permisos?.inventario_editar !== false && user?.rolNombre !== 'Cliente';
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const mapProduct = (row: any): Product => ({
    id: row.id,
    nombre: row.nombre,
    sku: row.sku,
    stock: row.stock,
    stockMinimo: row.stock_minimo ?? 0,
    precio: row.precio,
    subTotal: row.sub_total || 0,
    itbis: row.itbis || 0,
    proveedor: row.proveedor,
    fechaVencimiento: row.fecha_vencimiento || null
  });
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      // EMERGENCY: Single query with limit instead of while(hasMore) loop
      const {
        data,
        error
      } = await supabaseAdmin.from('products').select('*').order('nombre').limit(2000);
      if (!error && data) {
        setProducts(data.map(mapProduct));
      }
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);
  // Auto-refresh when products change in Supabase
  useRealtimeRefresh(['products'], fetchProducts);
  const generateSku = (currentProducts: Product[]) => {
    const existing = currentProducts.map(p => {
      const match = p.sku.match(new RegExp(`^${SKU_PREFIX}-(\\d+)$`, 'i'));
      return match ? parseInt(match[1], 10) : 0;
    }).filter(n => n > 0);
    const nextNum = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${SKU_PREFIX}-${String(nextNum).padStart(3, '0')}`;
  };
  const filteredProducts = products.filter(p => p.nombre.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase()) || p.proveedor.toLowerCase().includes(searchQuery.toLowerCase()));
  // Sorting setup
  const {
    items: sortedProducts,
    requestSort,
    sortConfig
  } = useSortableData(filteredProducts, {
    sku: p => p.sku,
    nombre: p => p.nombre,
    proveedor: p => p.proveedor,
    precio: p => p.precio,
    subTotal: p => p.subTotal || 0,
    itbis: p => p.itbis || 0,
    stock: p => p.stock,
    fechaVencimiento: p => p.fechaVencimiento || ''
  });
  const totalProductos = products.length;
  const sinStock = products.filter(p => p.stock === 0).length;
  const valorInventario = products.reduce((sum, p) => sum + p.stock * p.precio, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const totalPages = Math.ceil(sortedProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = sortedProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({
      ...emptyForm,
      sku: generateSku(products)
    });
    setIsModalOpen(true);
  };
  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      nombre: product.nombre,
      sku: product.sku,
      stock: String(product.stock),
      precio: String(product.precio),
      subTotal: String(product.subTotal || 0),
      itbis: String(product.itbis || 0),
      proveedor: product.proveedor,
      fechaVencimiento: product.fechaVencimiento || ''
    });
    setIsModalOpen(true);
  };
  const handleSave = async () => {
    if (!formData.nombre || !formData.sku || !formData.precio) return;
    setIsSaving(true);
    try {
      const payload = {
        nombre: formData.nombre,
        sku: formData.sku,
        stock: Number(formData.stock) || 0,
        precio: Number(formData.precio),
        sub_total: Number(formData.subTotal) || 0,
        itbis: Number(formData.itbis) || 0,
        proveedor: formData.proveedor,
        fecha_vencimiento: formData.fechaVencimiento || null
      };
      if (editingProduct) {
        const {
          data
        } = await supabaseAdmin.from('products').update(payload).eq('id', editingProduct.id).select().single();
        if (data) setProducts(prev => prev.map(p => p.id === editingProduct.id ? mapProduct(data) : p));
        addActivityLog('Editó producto', `Producto: ${formData.nombre} | SKU: ${formData.sku} | Stock: ${formData.stock}`);
      } else {
        const {
          data
        } = await supabaseAdmin.from('products').insert(payload).select().single();
        if (data) setProducts(prev => [mapProduct(data), ...prev]);
        addActivityLog('Creó producto', `Producto: ${formData.nombre} | SKU: ${formData.sku} | Precio: RD${formData.precio}`);
      }
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingProduct) return;
    addActivityLog('Eliminó producto', `Producto: ${editingProduct.nombre} | SKU: ${editingProduct.sku}`);
    await supabaseAdmin.from('products').delete().eq('id', editingProduct.id);
    setProducts(prev => prev.filter(p => p.id !== editingProduct.id));
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-2914" data-mp="36dfhl3bj23fn">
      <PageHeader title="Inventario" subtitle={canEdit ? 'Control de stock y productos' : 'Catálogo de productos disponibles'} icon={<PackageIcon className="w-6 h-6" data-id="element-2916" data-mp="3l9573bfx8yp4" />} data-id="element-2915" data-mp="2skpvyzzwv0j2">
        {canEdit && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2918" data-mp="3pagr91g10rpx" />} onClick={openAddModal} data-id="element-2917" data-mp="3l70v067vctdr">
            Nuevo Producto
          </Button>}
      </PageHeader>

      {/* Sin stock alert */}
      {sinStock > 0 && <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-xl animate-stagger-1" data-id="element-2919" data-mp="3g993vef1tbam">
          <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" data-id="element-2920" data-mp="1zuh9ivycdtyo" />
          <p className="text-sm text-amber-800 font-medium" data-id="element-2921" data-mp="24vohav0hit5c">
            {sinStock} producto{sinStock > 1 ? 's' : ''} sin existencia.
          </p>
        </div>}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 animate-stagger-1" data-id="element-2922" data-mp="2ribwxxuit1x2">
        <StatsCard title="Total Productos" value={totalProductos} icon={<PackageIcon className="w-5 h-5" data-id="element-2924" data-mp="2hlpetgtkwoyq" />} variant="primary" data-id="element-2923" data-mp="2mjqxw1r08oa7" />
        <StatsCard title="Sin Existencia" value={sinStock} icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-2926" data-mp="3fashcf1qd5zj" />} variant="danger" data-id="element-2925" data-mp="3f8y5dq8fso7l" />
        <StatsCard title="Valor Inventario" value={formatCurrency(valorInventario)} icon={<BarChart3Icon className="w-5 h-5" data-id="element-2928" data-mp="35gs39noh0qeo" />} variant="success" data-id="element-2927" data-mp="3aby0hinbpri2" />
      </div>

      {/* Search */}
      <div className="relative max-w-md animate-stagger-2" data-id="element-2929" data-mp="1trs9yxfmrar">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2930" data-mp="1vcmfnfu4883" />
        <input type="text" placeholder="Buscar por código, descripción o proveedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-2931" data-mp="3t7ebpg2e0cp7" />
      </div>

      {/* Product list */}
      <Card padding="none" className="animate-stagger-3" data-id="element-2932" data-mp="j9nmwi26fj2d">
        {filteredProducts.length === 0 ? <div className="p-8 sm:p-16 text-center animate-fade-in" data-id="element-2933" data-mp="o8qp7uqrvb6n">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-2934" data-mp="sb3u6gxp352d">
              <PackageIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-2935" data-mp="x58335qitn63" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-2936" data-mp="124cbvv9j9ivw">
              No hay productos
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-2937" data-mp="1c05w80thugo8">
              No se encontraron productos con los filtros aplicados
            </p>
          </div> : <>
            {/* ── MOBILE CARD VIEW (< sm) ── */}
            <div className="sm:hidden divide-y divide-slate-100" data-id="element-2938" data-mp="2vdf10a1f0r5d">
              {paginatedProducts.map(product => {
            const vencDate = product.fechaVencimiento ? new Date(product.fechaVencimiento + 'T00:00:00') : null;
            const now = new Date();
            const diffDays = vencDate ? Math.ceil((vencDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
            return <div key={product.id} className={`p-3.5 ${product.stock === 0 ? 'bg-rose-50/30' : ''}`} data-id="element-2939" data-mp="3o0sozin4ycdc">
                    <div className="flex items-start justify-between gap-2" data-id="element-2940" data-mp="1oq1eqnqq7hy6">
                      <div className="min-w-0 flex-1" data-id="element-2941" data-mp="1tnjp8ike658a">
                        <p className="font-semibold text-slate-900 text-sm leading-tight" data-id="element-2942" data-mp="1m38uns15o3ns">
                          {product.nombre}
                        </p>
                        <p className="text-xs text-[#2952C4] font-mono font-semibold mt-0.5" data-id="element-2943" data-mp="1hapeqoezwwft">
                          {product.sku}
                        </p>
                        {product.proveedor && <p className="text-[11px] text-slate-400 mt-0.5 truncate" data-id="element-2944" data-mp="17crsu7klzbqd">
                            {product.proveedor}
                          </p>}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-2945" data-mp="1v3x6cg32trxi">
                        <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-lg text-sm font-bold ${product.stock === 0 ? 'bg-rose-100 text-rose-600' : product.stock <= (product.stockMinimo || 5) ? 'bg-amber-100 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`} data-id="element-2946" data-mp="1v61jlomv83ag">
                          {product.stock}
                        </span>
                        {canEdit && <div className="flex items-center" data-id="element-2947" data-mp="m36nu5gb6w5f">
                            <button onClick={() => openEditModal(product)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-2948" data-mp="76xshezjh2ya">
                              <EditIcon className="w-4 h-4" data-id="element-2949" data-mp="3uhnngos9vy0v" />
                            </button>
                            <button onClick={() => {
                      setEditingProduct(product);
                      setIsDeleteModalOpen(true);
                    }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2950" data-mp="36xa5j9bk7ly">
                              <TrashIcon className="w-4 h-4" data-id="element-2951" data-mp="3foc17csppla1" />
                            </button>
                          </div>}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-xs" data-id="element-2952" data-mp="2s173afwkqr5n">
                      <span className="font-bold text-slate-900" data-id="element-2953" data-mp="3606p7fspcry4">
                        {formatCurrency(product.precio)}
                      </span>
                      {(product.subTotal || 0) > 0 && <span className="text-slate-500" data-id="element-2954" data-mp="3fuoyd0gw1fpb">
                          Sub: {formatCurrency(product.subTotal || 0)}
                        </span>}
                      {(product.itbis || 0) > 0 && <span className="text-slate-500" data-id="element-2955" data-mp="3knrr56ducl4g">
                          ITBIS: {formatCurrency(product.itbis || 0)}
                        </span>}
                      {vencDate && <span className={`ml-auto ${diffDays !== null && diffDays < 0 ? 'text-rose-600 font-semibold' : diffDays !== null && diffDays <= 90 ? 'text-amber-600 font-semibold' : 'text-slate-400'}`} data-id="element-2956" data-mp="3tp980qz2oqu7">
                          {diffDays !== null && diffDays < 0 ? '⚠ ' : ''}
                          {vencDate.toLocaleDateString('es-DO', {
                    day: '2-digit',
                    month: 'short',
                    year: '2-digit'
                  })}
                        </span>}
                    </div>
                  </div>;
          })}
            </div>

            {/* ── DESKTOP TABLE (sm+) ── */}
            <div className="hidden sm:block overflow-x-auto" data-id="element-2957" data-mp="cltxqe3zygg7">
              <table className="w-full" data-id="element-2958" data-mp="cn50calphi5q">
                <thead data-id="element-2959" data-mp="1j25xgjmgw6dw">
                  <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-2960" data-mp="1j2p7zf9sfshw">
                    <SortableTableHeader label="Código" sortKey="sku" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2961" data-mp="1o0qoptjqrfq7" />
                    <SortableTableHeader label="Descripción" sortKey="nombre" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2962" data-mp="2ysyhltpi25x9" />
                    <SortableTableHeader label="Proveedor" sortKey="proveedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2963" data-mp="2ksxfkaxi0sg7" />
                    <SortableTableHeader label="Precio" sortKey="precio" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2964" data-mp="e3iqexmdcf73" />
                    <SortableTableHeader label="Sub Total" sortKey="subTotal" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2965" data-mp="28cfjc3swg1jk" />
                    <SortableTableHeader label="ITBIS" sortKey="itbis" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2966" data-mp="2r9p35m24zh1c" />
                    <SortableTableHeader label="Existencia" sortKey="stock" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2967" data-mp="11tpppsrhj5fw" />
                    <SortableTableHeader label="Vencimiento" sortKey="fechaVencimiento" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2968" data-mp="tfuuac1jb9sl" />
                    {canEdit && <th className="px-5 py-4 w-20" data-id="element-2969" data-mp="anispripgcji"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-2970" data-mp="pbwus08arwz0">
                  {paginatedProducts.map(product => <tr key={product.id} className={`hover:bg-slate-50/50 transition-colors ${product.stock === 0 ? 'bg-rose-50/20' : ''}`} data-id="element-2971" data-mp="kl6mae4kre03">
                      <td className="px-5 py-4 font-mono text-sm font-semibold text-[#2952C4]" data-id="element-2972" data-mp="3o142b3pf3g6c">
                        {product.sku}
                      </td>
                      <td className="px-5 py-4 font-medium text-slate-900" data-id="element-2973" data-mp="37hxcdopgx7jx">
                        {product.nombre}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600" data-id="element-2974" data-mp="3cg8jhrodp3yg">
                        {product.proveedor}
                      </td>
                      <td className="px-5 py-4 text-right font-medium text-slate-900" data-id="element-2975" data-mp="3ggaczdjymr2n">
                        {formatCurrency(product.precio)}
                      </td>
                      <td className="px-5 py-4 text-right text-sm text-slate-700" data-id="element-2976" data-mp="3qf88x1jptmvo">
                        {formatCurrency(product.subTotal || 0)}
                      </td>
                      <td className="px-5 py-4 text-right text-sm text-slate-700" data-id="element-2977" data-mp="406ojgia0f43">
                        {formatCurrency(product.itbis || 0)}
                      </td>
                      <td className="px-5 py-4 text-center" data-id="element-2978" data-mp="d1mzbpd06j8u">
                        <span className={`font-bold text-lg ${product.stock === 0 ? 'text-rose-600' : 'text-slate-700'}`} data-id="element-2979" data-mp="d2674d8xvr95">
                          {product.stock}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600" data-id="element-2980" data-mp="1ok2310xwts4p">
                        {product.fechaVencimiento ? (() => {
                    const d = new Date(product.fechaVencimiento + 'T00:00:00');
                    const now = new Date();
                    const diffDays = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    const formatted = d.toLocaleDateString('es-DO', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    });
                    if (diffDays < 0) return <span className="text-rose-600 font-semibold" data-id="element-2981" data-mp="37v1vf8y5xfka">
                                  ⚠ {formatted}
                                </span>;
                    if (diffDays <= 90) return <span className="text-amber-600 font-semibold" data-id="element-2982" data-mp="3vkdyrs6t6teo">
                                  {formatted}
                                </span>;
                    return <span data-id="element-2983" data-mp="3lukqb01yquen">{formatted}</span>;
                  })() : <span className="text-slate-300" data-id="element-2984" data-mp="3gvrmy0ge4js7">—</span>}
                      </td>
                      {canEdit && <td className="px-5 py-4" data-id="element-2985" data-mp="dg2o3eype92y">
                          <div className="flex items-center gap-1" data-id="element-2986" data-mp="de9hfzo3bouy">
                            <button onClick={() => openEditModal(product)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-2987" data-mp="lp8ghwhud3vx">
                              <EditIcon className="w-4 h-4" data-id="element-2988" data-mp="zo820va252nv" />
                            </button>
                            <button onClick={() => {
                      setEditingProduct(product);
                      setIsDeleteModalOpen(true);
                    }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2989" data-mp="14naxbo08juin">
                              <TrashIcon className="w-4 h-4" data-id="element-2990" data-mp="1igk21bd8nruu" />
                            </button>
                          </div>
                        </td>}
                    </tr>)}
                </tbody>
              </table>
            </div>
          </>}
      </Card>

      <div className="animate-stagger-4" data-id="element-2991" data-mp="xpwzcfc8nauc">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredProducts.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-2992" data-mp="xr82qe1rugkx" />
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'} size="md" data-id="element-2993" data-mp="1leg78trdlllw">
        <ModalBody data-id="element-2994" data-mp="1vd5dtqdtnjmu">
          <div className="space-y-4" data-id="element-2995" data-mp="1vdoul3rqk1za">
            <div className="grid grid-cols-2 gap-4" data-id="element-2996" data-mp="1vcniqx2wbbgi">
              <Input label="Código (SKU)" value={formData.sku} onChange={e => setFormData({
              ...formData,
              sku: e.target.value
            })} placeholder="PRD-001" data-id="element-2997" data-mp="1qe2jazvgwvt4" />
              <Input label="Proveedor" value={formData.proveedor} onChange={e => setFormData({
              ...formData,
              proveedor: e.target.value
            })} placeholder="Ej: Laboratorios RD" data-id="element-2998" data-mp="27o6ho58lu87b" />
            </div>
            <Input label="Descripción" value={formData.nombre} onChange={e => setFormData({
            ...formData,
            nombre: e.target.value
          })} placeholder="Ej: Amoxicilina 500mg" data-id="element-2999" data-mp="1ownf1zihsw5t" />
            <div className="grid grid-cols-2 gap-4" data-id="element-3000" data-mp="1jyvbg88hjlls">
              <Input label="Precio (DOP)" type="number" value={formData.precio} onChange={e => setFormData({
              ...formData,
              precio: e.target.value
            })} placeholder="0" data-id="element-3001" data-mp="39d0fnkh89tiz" />
              <Input label="Existencia" type="number" value={formData.stock} onChange={e => setFormData({
              ...formData,
              stock: e.target.value
            })} placeholder="0" data-id="element-3002" data-mp="19bacahehssyr" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-3003" data-mp="qk10u2l5vu4j">
              <Input label="Sub Total (DOP)" type="number" value={formData.subTotal} onChange={e => setFormData({
              ...formData,
              subTotal: e.target.value
            })} placeholder="0" data-id="element-3004" data-mp="qig7uhehwj8s" />
              <Input label="ITBIS (DOP)" type="number" value={formData.itbis} onChange={e => setFormData({
              ...formData,
              itbis: e.target.value
            })} placeholder="0" data-id="element-3005" data-mp="7mq3won05cs9" />
            </div>
            <Input label="Fecha de Vencimiento" type="date" value={formData.fechaVencimiento} onChange={e => setFormData({
            ...formData,
            fechaVencimiento: e.target.value
          })} data-id="element-3006" data-mp="2x91mivjgbjpb" />
          </div>
        </ModalBody>
        <ModalFooter data-id="element-3007" data-mp="29h4elfj4hbth">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-3008" data-mp="2e9ffolpln3nm">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.nombre || !formData.sku || !formData.precio} data-id="element-3009" data-mp="1hskqpc18mhsr">
            {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Producto" size="sm" data-id="element-3010" data-mp="2k4e6g2zeit46">
        <ModalBody data-id="element-3011" data-mp="2tuh0m6i9ed43">
          <p className="text-slate-600" data-id="element-3012" data-mp="2tw1urv0nvu1f">
            ¿Eliminar <strong data-id="element-3013" data-mp="2txd0w1hkxdvp">{editingProduct?.nombre}</strong>? Esta acción no
            se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-3014" data-mp="216v1bag2dun1">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-3015" data-mp="217mtebagda8k">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-3016" data-mp="2b4r7roy241et">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}